import { useState, useMemo, useCallback } from 'react'
import { DayPicker, DayClickEventHandler } from 'react-day-picker'
import { format } from 'date-fns'
import { RefreshCw, Loader2, Calendar } from 'lucide-react'
import { usePhotoIndexContext } from './PhotoIndexContext'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import 'react-day-picker/dist/style.css'

interface CalendarPanelProps {
  onDateSelect?: (date: Date) => void
  selectedDate?: Date | null
}

export function CalendarPanel({ onDateSelect, selectedDate }: CalendarPanelProps) {
  const [month, setMonth] = useState(new Date())
  const {
    index,
    isLoading,
    lastUpdated,
    fetchIndex,
    rebuild,
    getDatesWithPhotos,
  } = usePhotoIndexContext()

  // Convert MM-DD dates to Date objects for the current year
  const datesWithPhotos = useMemo(() => {
    const dates = getDatesWithPhotos()
    const currentYear = new Date().getFullYear()

    return dates.map(dateKey => {
      // dateKey is in MM-DD format
      const [month, day] = dateKey.split('-').map(Number)
      return new Date(currentYear, month - 1, day)
    })
  }, [getDatesWithPhotos])

  const handleDayClick: DayClickEventHandler = (day) => {
    onDateSelect?.(day)
  }

  const handleTodayClick = useCallback(() => {
    const today = new Date()
    setMonth(today)
    onDateSelect?.(today)
  }, [onDateSelect])

  // Custom day content to show indicator for days with photos
  const modifiers = {
    hasPhotos: datesWithPhotos,
  }

  const modifiersStyles = {
    hasPhotos: {
      position: 'relative' as const,
    },
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Calendar</h2>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleTodayClick}
            title="Go to today"
          >
            <Calendar className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchIndex}
            disabled={isLoading}
            title="Refresh index"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Calendar */}
      <div className="calendar-container">
        <DayPicker
          mode="single"
          selected={selectedDate || undefined}
          onSelect={(day) => day && handleDayClick(day, {}, {} as React.MouseEvent)}
          month={month}
          onMonthChange={setMonth}
          modifiers={modifiers}
          modifiersStyles={modifiersStyles}
          showOutsideDays
          formatters={{
            formatCaption: (date) => format(date, 'MMMM'), // Month only, no year
          }}
          className="!bg-transparent"
          classNames={{
            months: 'flex flex-col',
            month: 'space-y-4',
            caption: 'flex justify-center pt-1 relative items-center text-white',
            caption_label: 'text-sm font-medium',
            nav: 'space-x-1 flex items-center',
            nav_button: cn(
              'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
              'inline-flex items-center justify-center rounded-md text-white'
            ),
            nav_button_previous: 'absolute left-1',
            nav_button_next: 'absolute right-1',
            table: 'w-full border-collapse space-y-1',
            head_row: 'flex',
            head_cell: 'text-zinc-500 rounded-md w-9 font-normal text-[0.8rem]',
            row: 'flex w-full mt-2',
            cell: cn(
              'relative p-0 text-center text-sm focus-within:relative focus-within:z-20',
              'first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
            ),
            day: cn(
              'h-9 w-9 p-0 font-normal',
              'inline-flex items-center justify-center rounded-md',
              'text-zinc-300 hover:bg-zinc-800 hover:text-white',
              'focus:outline-none focus:ring-2 focus:ring-zinc-500'
            ),
            day_selected: 'bg-white text-zinc-900 hover:bg-zinc-100 hover:text-zinc-900',
            day_today: 'bg-zinc-800 text-white',
            day_outside: 'text-zinc-600 opacity-50',
            day_disabled: 'text-zinc-600 opacity-50',
            day_hidden: 'invisible',
          }}
          components={{
            DayContent: ({ date }) => {
              const dateKey = format(date, 'MM-dd')
              const hasPhoto = index[dateKey]?.photos?.length > 0
              const photoCount = index[dateKey]?.photos?.length || 0

              return (
                <div className="relative w-full h-full flex items-center justify-center">
                  <span>{date.getDate()}</span>
                  {hasPhoto && (
                    <span
                      className={cn(
                        'absolute bottom-0.5 left-1/2 -translate-x-1/2',
                        'w-1.5 h-1.5 rounded-full',
                        photoCount > 1 ? 'bg-blue-500' : 'bg-green-500'
                      )}
                      title={`${photoCount} photo${photoCount > 1 ? 's' : ''}`}
                    />
                  )}
                </div>
              )
            },
          }}
        />
      </div>

      {/* Stats */}
      <div className="text-xs text-zinc-500 space-y-1">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green-500" />
          <span>1 photo</span>
          <span className="w-2 h-2 rounded-full bg-blue-500 ml-2" />
          <span>Multiple photos</span>
        </div>
        {lastUpdated && (
          <p>Last updated: {format(lastUpdated, 'HH:mm:ss')}</p>
        )}
        <p>{getDatesWithPhotos().length} dates with photos</p>
      </div>

      {/* Rebuild button */}
      <Button
        variant="outline"
        size="sm"
        onClick={rebuild}
        disabled={isLoading}
        className="w-full"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Rebuilding...
          </>
        ) : (
          <>
            <RefreshCw className="w-4 h-4 mr-2" />
            Rebuild Index
          </>
        )}
      </Button>
    </div>
  )
}
